import { useState } from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { DollarSign, X } from "lucide-react";
import { formatCurrency, parseSalaryInput } from "@/lib/salaryUtils";

interface SalaryInputProps {
  onSalaryChange: (salary: number | null) => void;
  currentSalary?: number | null;
  placeholder?: string;
  className?: string;
}

const SalaryInput = ({ 
  onSalaryChange, 
  currentSalary, 
  placeholder = "e.g., 75000 or 75k",
  className = ""
}: SalaryInputProps) => {
  const [inputValue, setInputValue] = useState(
    currentSalary ? formatCurrency(currentSalary).replace('$', '') : ''
  );
  const [isValid, setIsValid] = useState(true);

  const handleInputChange = (value: string) => {
    setInputValue(value);
    
    if (!value.trim()) {
      setIsValid(true);
      onSalaryChange(null);
      return;
    }

    const parsed = parseSalaryInput(value);
    if (parsed !== null && parsed > 0) {
      setIsValid(true);
      onSalaryChange(parsed);
    } else {
      setIsValid(false);
    }
  };

  const clearInput = () => {
    setInputValue('');
    setIsValid(true);
    onSalaryChange(null);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <Label htmlFor="salary-input" className="text-sm font-medium">
        Enter your salary to see your percentile
      </Label>
      <div className="relative">
        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          id="salary-input"
          type="text"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          placeholder={placeholder}
          className={`pl-10 pr-10 ${!isValid ? 'border-destructive' : ''}`}
        />
        {inputValue && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearInput}
            className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-muted"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>
      {!isValid && (
        <p className="text-sm text-destructive">
          Please enter a valid salary amount
        </p>
      )}
      {currentSalary && (
        <p className="text-xs text-muted-foreground">
          Current: {formatCurrency(currentSalary)}
        </p>
      )}
    </div>
  );
};

export default SalaryInput;